import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import portalApi from "../../api/portalClient";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useBranding } from "../../context/BrandingContext";
import { Ticket, PortalInvoiceSummary, PortalDevice } from "../../types";

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const branding = useBranding();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [devices, setDevices] = useState<PortalDevice[]>([]);
  const [invoices, setInvoices] = useState<PortalInvoiceSummary[]>([]);
  // Hide the "Last Statement" tile entirely when this portal user is
  // denied invoice access. Also skip the fetch so it doesn't 403 and
  // surface as an uncaught promise rejection in the runtime overlay.
  const canViewInvoices = user?.can_view_invoices !== false;

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/tickets")
      .then((r) => setTickets(r.data))
      .catch(() => {});
    portalApi
      .get("/api/v1/portal/devices")
      .then((r) => setDevices(r.data))
      .catch(() => {});
    if (canViewInvoices) {
      portalApi
        .get("/api/v1/portal/invoices")
        .then((r) => setInvoices(r.data))
        .catch(() => {});
    }
  }, [canViewInvoices]);

  const open = tickets.filter((t) => t.status !== "closed" && t.status !== "resolved");
  const waiting = tickets.filter((t) => t.waiting_on_customer);
  const onlineCount = devices.filter((d) => d.status === "online").length;
  const recentInvoice = invoices[0];

  return (
    <div className="max-w-6xl">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
        Welcome, {(user?.full_name || user?.email)?.split("@")[0]}
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-7">
        Here's a quick view of your services with {branding.company_name}.
      </p>

      <div
        className={`grid grid-cols-1 gap-4 mb-7 ${canViewInvoices ? "md:grid-cols-4" : "md:grid-cols-3"}`}
      >
        <Card
          label="Open Tickets"
          value={String(open.length)}
          link="/portal/tickets"
          accent="bg-blue-500"
          tone="text-blue-700 dark:text-blue-400"
        />
        <Card
          label="Waiting On You"
          value={String(waiting.length)}
          link="/portal/tickets"
          accent="bg-purple-500"
          tone="text-purple-700 dark:text-purple-400"
          urgent={waiting.length > 0}
        />
        <Card
          label="Devices Online"
          value={`${onlineCount} / ${devices.length}`}
          link="/portal/devices"
          accent="bg-emerald-500"
          tone="text-emerald-700 dark:text-emerald-400"
        />
        {canViewInvoices && (
          <Card
            label="Last Statement"
            value={
              recentInvoice
                ? `$${(recentInvoice.billable_amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "—"
            }
            link="/portal/invoices"
            accent="bg-brand"
            tone="text-brand dark:text-brand-light"
            caption={recentInvoice?.period_label}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Recent Tickets" link="/portal/tickets" linkLabel="View all">
          {open.length === 0 && <p className="text-gray-400 dark:text-gray-500 text-sm">No open tickets.</p>}
          {open.slice(0, 6).map((t) => (
            <Link
              key={t.id}
              to={`/portal/tickets/${t.id}`}
              className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-2 px-2 rounded"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  #{t.id} {t.title}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {t.priority} · {t.status.replace("_", " ")}
                </div>
              </div>
              {t.waiting_on_customer && (
                <span className="ml-2 text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                  Waiting on you
                </span>
              )}
            </Link>
          ))}
        </Section>

        <Section title="Your Devices" link="/portal/devices" linkLabel="View all">
          {devices.length === 0 && (
            <p className="text-gray-400 dark:text-gray-500 text-sm">No devices reported yet.</p>
          )}
          {devices.slice(0, 6).map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{d.hostname}</div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${d.status === "online" ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
              >
                {d.status}
              </span>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Card({
  label,
  value,
  link,
  accent,
  tone,
  caption,
  urgent,
}: {
  label: string;
  value: string;
  link: string;
  accent: string;
  tone: string;
  caption?: string;
  urgent?: boolean;
}) {
  return (
    <Link
      to={link}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 block overflow-hidden relative group"
    >
      <div className={`absolute top-0 left-0 w-1 h-full ${accent}`} />
      <div className="p-5 pl-6">
        <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
          {label}
          {urgent && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 normal-case tracking-normal font-medium">
              action needed
            </span>
          )}
        </div>
        <div className={`text-3xl font-bold ${tone} leading-none`}>{value}</div>
        {caption && <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">{caption}</div>}
      </div>
    </Link>
  );
}

function Section({
  title,
  link,
  linkLabel,
  children,
}: {
  title: string;
  link: string;
  linkLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
        <Link
          to={link}
          className="text-xs text-brand dark:text-brand-light hover:underline font-medium"
        >
          {linkLabel} →
        </Link>
      </div>
      <div className="px-5 py-3">{children}</div>
    </div>
  );
}
